import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../../components/Header';
import BreadcrumbNavigation from '../../components/BreadcrumbNavigation';
import QuickActionToolbar from '../../components/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import { clientService } from '../../services/clientService';
import NewClientModal from '../client-management-system/components/NewClientModal';
import { calculateAge, formatDateBR, formatCPF, isValidCPFFormat, isPassportExpired, getDaysUntilExpiration } from '../../utils/dateHelpers';

export default function EnhancedClientManagement() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isAddingPassenger, setIsAddingPassenger] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
    notes: '',
    birthDate: '',
    cpf: '',
    passportNumber: '',
    passportValidity: ''
  });

  const [passengerForm, setPassengerForm] = useState({
    fullName: '',
    birthDate: '',
    document: '',
    phone: '',
    email: '',
    notes: '',
    cpf: '',
    passportNumber: '',
    passportValidity: ''
  });

  // Optimize query limits for faster loading
  const ITEMS_PER_PAGE = 25; // Already optimized

  // Optimized client loading with useCallback
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await clientService?.getAll();
      setClients(data || []);
      return data;
    } catch (err) {
      setError(err?.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Optimized passenger loading with lazy loading
  const loadPassengers = useCallback(async (clientId) => {
    if (!clientId) return;
    try {
      const data = await clientService?.getPassengers(clientId);
      setPassengers(data || []);
    } catch (err) {
      console.error('Error loading passengers:', err);
      setPassengers([]);
    }
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadPassengers(selectedClient?.id);
      setClientForm({
        name: selectedClient?.name || '',
        email: selectedClient?.email || '',
        phone: selectedClient?.phone || '',
        document: selectedClient?.document || '',
        address: selectedClient?.address || '',
        notes: selectedClient?.notes || '',
        birthDate: selectedClient?.birth_date || '',
        cpf: selectedClient?.cpf || '',
        passportNumber: selectedClient?.passport_number || '',
        passportValidity: selectedClient?.passport_validity || ''
      });
    }
  }, [selectedClient, loadPassengers]);

  // Ensure filtering is memoized (already implemented)
  const filteredClients = useMemo(() => {
    let filtered = clients?.filter(client =>
      client?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      client?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    ) || [];

    const total = Math.ceil(filtered?.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginated = filtered?.slice(startIndex, startIndex + itemsPerPage);

    return {
      filteredClients: filtered,
      totalPages: total,
      paginatedClients: paginated
    };
  }, [clients, searchTerm, currentPage, itemsPerPage]);

  // Add this block - Extract values from filteredClients object
  const { paginatedClients, totalPages } = filteredClients;

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleEditClient = async () => {
    if (!selectedClient) return;

    try {
      await clientService?.update(selectedClient?.id, clientForm);
      const updatedClients = await loadClients();
      const updated = updatedClients?.find(c => c?.id === selectedClient?.id);
      if (updated) {
        setSelectedClient(updated);
      }
      setIsEditingClient(false);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAddPassenger = async () => {
    if (!selectedClient) return;

    try {
      await clientService?.addPassenger({
        clientId: selectedClient?.id,
        ...passengerForm
      });
      await loadPassengers(selectedClient?.id);
      setIsAddingPassenger(false);
      setPassengerForm({
        fullName: '',
        birthDate: '',
        document: '',
        phone: '',
        email: '',
        notes: '',
        cpf: '',
        passportNumber: '',
        passportValidity: ''
      });
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleEditPassenger = async () => {
    if (!editingPassenger) return;

    try {
      await clientService?.updatePassenger(editingPassenger?.id, passengerForm);
      await loadPassengers(selectedClient?.id);
      setEditingPassenger(null);
      setPassengerForm({
        fullName: '',
        birthDate: '',
        document: '',
        phone: '',
        email: '',
        notes: '',
        cpf: '',
        passportNumber: '',
        passportValidity: ''
      });
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeletePassenger = async (passengerId) => {
    if (!confirm('Tem certeza que deseja remover este passageiro?')) return;

    try {
      await clientService?.deletePassenger(passengerId);
      await loadPassengers(selectedClient?.id);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleNewClient = async (newClient) => {
    await loadClients();
    setSelectedClient(newClient);
    console.log('Cliente cadastrado e selecionado:', newClient);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground">Carregando clientes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="main-content">
        <BreadcrumbNavigation />
        
        {/* Page Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Icon name="Users" size={22} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Gestão de Clientes</h1>
                <p className="text-sm text-muted-foreground">
                  Gerenciamento completo de clientes e passageiros ({filteredClients?.length} clientes)
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowNewClientModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Icon name="UserPlus" size={18} />
              <span>Novo Cliente</span>
            </button>
          </div>
        </div>

        <div className="flex h-[calc(100vh-240px)]">
          {/* Clients List with Pagination */}
          <div className="w-[40%] border-r border-border flex flex-col">
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {paginatedClients?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <Icon name="Users" size={48} className="text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhum cliente encontrado</p>
                </div>
              ) : (
                paginatedClients?.map((client) => (
                  <div
                    key={client?.id}
                    onClick={() => setSelectedClient(client)}
                    className={`p-4 border-b border-border cursor-pointer hover:bg-muted/30 transition-colors ${
                      selectedClient?.id === client?.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-semibold">
                        {client?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{client?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{client?.email || 'Sem email'}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-border bg-card">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-lg flex items-center gap-1 ${
                      currentPage === 1
                        ? 'text-muted-foreground cursor-not-allowed'
                        : 'text-primary hover:bg-primary/10'
                    }`}
                  >
                    <Icon name="ChevronLeft" size={16} />
                    <span>Anterior</span>
                  </button>
                  
                  <span className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-lg flex items-center gap-1 ${
                      currentPage === totalPages
                        ? 'text-muted-foreground cursor-not-allowed'
                        : 'text-primary hover:bg-primary/10'
                    }`}
                  >
                    <span>Próxima</span>
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Client Details */}
          <div className="flex-1 overflow-y-auto">
            {selectedClient ? (
              <div className="p-6">
                {/* Client Information */}
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-foreground">Informações do Cliente</h2>
                    <button
                      onClick={() => setIsEditingClient(!isEditingClient)}
                      className="text-primary hover:text-primary/80 flex items-center gap-2"
                    >
                      <Icon name={isEditingClient ? 'X' : 'Edit'} size={18} />
                      <span>{isEditingClient ? 'Cancelar' : 'Editar'}</span>
                    </button>
                  </div>

                  {isEditingClient ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Nome</label>
                          <input
                            type="text"
                            value={clientForm?.name}
                            onChange={(e) => setClientForm({ ...clientForm, name: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                          <input
                            type="email"
                            value={clientForm?.email}
                            onChange={(e) => setClientForm({ ...clientForm, email: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Telefone</label>
                          <input
                            type="text"
                            value={clientForm?.phone}
                            onChange={(e) => setClientForm({ ...clientForm, phone: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                            Data de Nascimento
                            {clientForm?.birthDate && (
                              <span className="text-xs text-primary">
                                ({calculateAge(clientForm?.birthDate)} anos)
                              </span>
                            )}
                          </label>
                          <input
                            type="date"
                            value={clientForm?.birthDate}
                            onChange={(e) => setClientForm({ ...clientForm, birthDate: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            max={new Date()?.toISOString()?.split('T')?.[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">CPF</label>
                          <input
                            type="text"
                            value={clientForm?.cpf}
                            onChange={(e) => setClientForm({ ...clientForm, cpf: formatCPF(e?.target?.value) })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            placeholder="000.000.000-00"
                            maxLength={14}
                          />
                          {clientForm?.cpf && !isValidCPFFormat(clientForm?.cpf) && (
                            <p className="text-warning text-xs mt-1">Formato de CPF inválido</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Documento (RG/Outros)</label>
                          <input
                            type="text"
                            value={clientForm?.document}
                            onChange={(e) => setClientForm({ ...clientForm, document: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Número do Passaporte</label>
                          <input
                            type="text"
                            value={clientForm?.passportNumber}
                            onChange={(e) => setClientForm({ ...clientForm, passportNumber: e?.target?.value?.toUpperCase() })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            placeholder="XX000000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Validade do Passaporte</label>
                          <input
                            type="date"
                            value={clientForm?.passportValidity}
                            onChange={(e) => setClientForm({ ...clientForm, passportValidity: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            min={new Date()?.toISOString()?.split('T')?.[0]}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Endereço</label>
                        <input
                          type="text"
                          value={clientForm?.address}
                          onChange={(e) => setClientForm({ ...clientForm, address: e?.target?.value })}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Observações</label>
                        <textarea
                          value={clientForm?.notes}
                          onChange={(e) => setClientForm({ ...clientForm, notes: e?.target?.value })}
                          rows={3}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                        />
                      </div>
                      <button
                        onClick={handleEditClient}
                        className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg"
                      >
                        Salvar Alterações
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Nome</p>
                        <p className="text-sm text-foreground">{selectedClient?.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Email</p>
                        <p className="text-sm text-foreground">{selectedClient?.email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Telefone</p>
                        <p className="text-sm text-foreground">{selectedClient?.phone || '-'}</p>
                      </div>
                      {selectedClient?.birth_date && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Data de Nascimento / Idade</p>
                          <p className="text-sm text-foreground">
                            {formatDateBR(selectedClient?.birth_date)} 
                            <span className="text-primary ml-2">({calculateAge(selectedClient?.birth_date)} anos)</span>
                          </p>
                        </div>
                      )}
                      {selectedClient?.cpf && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">CPF</p>
                          <p className="text-sm text-foreground">{formatCPF(selectedClient?.cpf)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Documento (RG/Outros)</p>
                        <p className="text-sm text-foreground">{selectedClient?.document || '-'}</p>
                      </div>
                      {selectedClient?.passport_number && (
                        <>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Número do Passaporte</p>
                            <p className="text-sm text-foreground">{selectedClient?.passport_number}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Validade do Passaporte</p>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-foreground">{formatDateBR(selectedClient?.passport_validity)}</p>
                              {isPassportExpired(selectedClient?.passport_validity) ? (
                                <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">Expirado</span>
                              ) : getDaysUntilExpiration(selectedClient?.passport_validity) <= 90 && (
                                <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded">
                                  Expira em {getDaysUntilExpiration(selectedClient?.passport_validity)} dias
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">Endereço</p>
                        <p className="text-sm text-foreground">{selectedClient?.address || '-'}</p>
                      </div>
                      {selectedClient?.notes && (
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground mb-1">Observações</p>
                          <p className="text-sm text-foreground">{selectedClient?.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Passengers Section */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-foreground">Passageiros</h2>
                    <button
                      onClick={() => setIsAddingPassenger(true)}
                      className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Icon name="Plus" size={18} />
                      <span>Adicionar Passageiro</span>
                    </button>
                  </div>

                  {(isAddingPassenger || editingPassenger) && (
                    <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-foreground mb-4">
                        {editingPassenger ? 'Editar Passageiro' : 'Novo Passageiro'}
                      </h3>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Nome Completo</label>
                          <input
                            type="text"
                            value={passengerForm?.fullName}
                            onChange={(e) => setPassengerForm({ ...passengerForm, fullName: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                            Data de Nascimento
                            {passengerForm?.birthDate && (
                              <span className="text-xs text-primary">
                                ({calculateAge(passengerForm?.birthDate)} anos)
                              </span>
                            )}
                          </label>
                          <input
                            type="date"
                            value={passengerForm?.birthDate}
                            onChange={(e) => setPassengerForm({ ...passengerForm, birthDate: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            max={new Date()?.toISOString()?.split('T')?.[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">CPF</label>
                          <input
                            type="text"
                            value={passengerForm?.cpf}
                            onChange={(e) => setPassengerForm({ ...passengerForm, cpf: formatCPF(e?.target?.value) })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            placeholder="000.000.000-00"
                            maxLength={14}
                          />
                          {passengerForm?.cpf && !isValidCPFFormat(passengerForm?.cpf) && (
                            <p className="text-warning text-xs mt-1">Formato de CPF inválido</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Documento (RG/Outros)</label>
                          <input
                            type="text"
                            value={passengerForm?.document}
                            onChange={(e) => setPassengerForm({ ...passengerForm, document: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Telefone</label>
                          <input
                            type="text"
                            value={passengerForm?.phone}
                            onChange={(e) => setPassengerForm({ ...passengerForm, phone: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                          <input
                            type="email"
                            value={passengerForm?.email}
                            onChange={(e) => setPassengerForm({ ...passengerForm, email: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Número do Passaporte</label>
                          <input
                            type="text"
                            value={passengerForm?.passportNumber}
                            onChange={(e) => setPassengerForm({ ...passengerForm, passportNumber: e?.target?.value?.toUpperCase() })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            placeholder="XX000000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Validade do Passaporte</label>
                          <input
                            type="date"
                            value={passengerForm?.passportValidity}
                            onChange={(e) => setPassengerForm({ ...passengerForm, passportValidity: e?.target?.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                            min={new Date()?.toISOString()?.split('T')?.[0]}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-foreground mb-2">Observações</label>
                          <textarea
                            value={passengerForm?.notes}
                            onChange={(e) => setPassengerForm({ ...passengerForm, notes: e?.target?.value })}
                            rows={2}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={editingPassenger ? handleEditPassenger : handleAddPassenger}
                          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg"
                        >
                          {editingPassenger ? 'Salvar Alterações' : 'Adicionar'}
                        </button>
                        <button
                          onClick={() => {
                            setIsAddingPassenger(false);
                            setEditingPassenger(null);
                            setPassengerForm({
                              fullName: '',
                              birthDate: '',
                              document: '',
                              phone: '',
                              email: '',
                              notes: '',
                              cpf: '',
                              passportNumber: '',
                              passportValidity: ''
                            });
                          }}
                          className="border border-border hover:bg-muted/50 px-4 py-2 rounded-lg"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {passengers?.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Nenhum passageiro cadastrado</p>
                      </div>
                    ) : (
                      passengers?.map((passenger) => (
                        <div key={passenger?.id} className="bg-background border border-border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">{passenger?.full_name}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                                {passenger?.birth_date && (
                                  <p className="text-xs text-muted-foreground">
                                    Nascimento: {formatDateBR(passenger?.birth_date)} 
                                    <span className="text-primary ml-1">({calculateAge(passenger?.birth_date)} anos)</span>
                                  </p>
                                )}
                                {passenger?.cpf && (
                                  <p className="text-xs text-muted-foreground">CPF: {formatCPF(passenger?.cpf)}</p>
                                )}
                                {passenger?.document && (
                                  <p className="text-xs text-muted-foreground">Doc: {passenger?.document}</p>
                                )}
                                {passenger?.phone && (
                                  <p className="text-xs text-muted-foreground">Tel: {passenger?.phone}</p>
                                )}
                                {passenger?.email && (
                                  <p className="text-xs text-muted-foreground">Email: {passenger?.email}</p>
                                )}
                                {passenger?.passport_number && (
                                  <p className="text-xs text-muted-foreground">Passaporte: {passenger?.passport_number}</p>
                                )}
                                {passenger?.passport_validity && (
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs text-muted-foreground">
                                      Validade: {formatDateBR(passenger?.passport_validity)}
                                    </p>
                                    {isPassportExpired(passenger?.passport_validity) && (
                                      <span className="text-xs bg-error/10 text-error px-1.5 py-0.5 rounded">Expirado</span>
                                    )}
                                    {!isPassportExpired(passenger?.passport_validity) && 
                                     getDaysUntilExpiration(passenger?.passport_validity) <= 90 && (
                                      <span className="text-xs bg-warning/10 text-warning px-1.5 py-0.5 rounded">
                                        {getDaysUntilExpiration(passenger?.passport_validity)}d
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {passenger?.notes && (
                                <p className="text-xs text-muted-foreground mt-2">{passenger?.notes}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingPassenger(passenger);
                                  setPassengerForm({
                                    fullName: passenger?.full_name || '',
                                    birthDate: passenger?.birth_date || '',
                                    document: passenger?.document || '',
                                    phone: passenger?.phone || '',
                                    email: passenger?.email || '',
                                    notes: passenger?.notes || '',
                                    cpf: passenger?.cpf || '',
                                    passportNumber: passenger?.passport_number || '',
                                    passportValidity: passenger?.passport_validity || ''
                                  });
                                }}
                                className="text-primary hover:text-primary/80 p-2"
                              >
                                <Icon name="Edit" size={16} />
                              </button>
                              <button
                                onClick={() => handleDeletePassenger(passenger?.id)}
                                className="text-error hover:text-error/80 p-2"
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <Icon name="UserCircle" size={64} className="text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Selecione um cliente para ver detalhes</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickActionToolbar />
      <NewClientModal
        isOpen={showNewClientModal}
        onClose={() => setShowNewClientModal(false)}
        onSave={handleNewClient}
      />
    </div>
  );
}